/**
 * StoreLocationPanel — vendor edits their store's public location (address +
 * lat/lng). This drives driver matching, customer distance calculation, and
 * the delivery-radius check. Uses Google Places autocomplete when available
 * and falls back to a plain input when no key is configured.
 *
 * Approval gating rules (per platform admin request):
 *   • 1st edit  → applied instantly.
 *   • 2nd edit+ → stashed as `pending_location`, store is frozen from taking
 *                 new orders, and a platform admin must approve/reject the change.
 */
import React, { useEffect, useState } from 'react';
import { MapPin, Save, Loader2, ShieldAlert, Clock, XCircle } from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { vendorAdminApi } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const StoreLocationPanel = ({ vendorId, impersonating }) => {
  const vid = impersonating ? vendorId : undefined;
  const [store, setStore] = useState(null);
  const [draft, setDraft] = useState({ address: '', lat: null, lng: null });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const loadStore = async () => {
    try {
      const s = await vendorAdminApi.store(vid);
      setStore(s);
      setDraft({
        address: s.address || '',
        lat: s.lat ?? null,
        lng: s.lng ?? null,
      });
      setDirty(false);
    } catch (_e) {
      setStore({});
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      const s = await vendorAdminApi.store(vid).catch(() => ({}));
      if (!alive) return;
      setStore(s);
      setDraft({
        address: s.address || '',
        lat: s.lat ?? null,
        lng: s.lng ?? null,
      });
    })();
    return () => { alive = false; };
  }, [vid]);

  const onAddressSelect = (v) => {
    setDraft({
      address: v.address || v.formatted_address || '',
      lat: v.lat ?? null,
      lng: v.lng ?? null,
    });
    setDirty(true);
  };

  const save = async () => {
    if (!draft.lat || !draft.lng) {
      toast.error('Pick an address from the suggestions so we get precise coordinates.');
      return;
    }
    setSaving(true);
    try {
      const geo = await reverseGeocode(draft.lat, draft.lng);
      const r = await vendorAdminApi.updateStore({
        address: draft.address,
        lat: draft.lat,
        lng: draft.lng,
        ...(geo || {}),
      }, vid);
      setStore(r);
      setDirty(false);
      if (r?.location_approval_status === 'pending') {
        toast.success('Change submitted — a platform admin will review your new store location. New orders are paused until it\'s approved.');
      } else {
        const parts = [geo?.city, geo?.state, geo?.country].filter(Boolean).join(', ');
        toast.success(parts
          ? `Store location updated (${parts}). Drivers and customers will use this from now on.`
          : 'Store location updated. Drivers and customers will use this from now on.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=14`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
      if (!res.ok) return null;
      const data = await res.json();
      const a = data?.address || {};
      const city = a.city || a.town || a.village || a.municipality || a.hamlet || a.suburb || null;
      return {
        country: a.country || null,
        state: a.state || a.region || null,
        city,
      };
    } catch (_e) {
      return null;
    }
  };

  if (!store) {
    return (
      <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading store location…
      </section>
    );
  }

  const mapKey = (window && window.__ENV__?.GOOGLE_MAPS_KEY) || null;
  const hasCoords = draft.lat != null && draft.lng != null;
  const isPending = store.location_approval_status === 'pending';
  const wasRejected = store.location_approval_status === 'rejected';
  const editsCount = Number(store.location_edits_count || 0);
  const willNeedApproval = editsCount >= 1 && !impersonating;
  const pending = store.pending_location || null;

  return (
    <section
      className="mt-6 bg-white border border-slate-200 rounded-2xl p-5"
      data-testid="store-location-panel"
    >
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="h-5 w-5 text-emerald-600" />
        <h2 className="font-semibold text-slate-900">Store location</h2>
      </div>
      <p className="text-sm text-slate-500 mb-3">
        This is the exact point drivers pick up from and the origin of the delivery-radius check
        customers see on your storefront. Search for the address and pick from the suggestions to
        capture accurate coordinates.
      </p>

      {isPending && (
        <div
          className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2"
          data-testid="store-location-pending-banner"
        >
          <Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold">Awaiting platform admin approval</div>
            <div className="mt-0.5">
              You already changed your store location once. This new change is pending review — new
              orders are paused for this store until an admin approves it. You can&apos;t edit again
              until the pending change is resolved.
            </div>
            {pending?.address && (
              <div className="mt-2 text-xs text-amber-800 bg-white/60 rounded-md p-2">
                <div className="font-semibold uppercase tracking-wider text-[10px] text-amber-700">Submitted address</div>
                <div>{pending.address}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {wasRejected && !isPending && (
        <div
          className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-3 flex items-start gap-2"
          data-testid="store-location-rejected-banner"
        >
          <XCircle className="h-4 w-4 text-rose-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-rose-900">
            <div className="font-semibold">Previous location change was rejected</div>
            {store.location_rejection_reason && (
              <div className="mt-0.5 italic">&ldquo;{store.location_rejection_reason}&rdquo;</div>
            )}
            <div className="mt-1">You can submit a new change below.</div>
          </div>
        </div>
      )}

      {!isPending && willNeedApproval && (
        <div
          className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-start gap-2 text-xs text-slate-600"
          data-testid="store-location-approval-notice"
        >
          <ShieldAlert className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-semibold text-slate-800">Heads-up:</span> your first location edit
            was already used. This next change will need platform admin approval before it goes live —
            new orders will be paused until it&apos;s reviewed.
          </div>
        </div>
      )}

      <AddressAutocomplete
        value={draft.address}
        onChange={(v) => { setDraft((d) => ({ ...d, address: v })); setDirty(true); }}
        onSelect={onAddressSelect}
        placeholder="123 Bank St, Ottawa, ON"
        disabled={isPending}
        data-testid="store-address-input"
      />
      <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
        <span data-testid="store-edits-count" className="ml-auto text-slate-400">
          Edits used: {editsCount}
        </span>
      </div>
      {hasCoords && (
        <div className="mt-4 rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
          <img
            src={`https://maps.googleapis.com/maps/api/staticmap?center=${draft.lat},${draft.lng}&zoom=15&size=800x260&markers=color:0x059669%7C${draft.lat},${draft.lng}${mapKey ? `&key=${mapKey}` : ''}`}
            alt="Store location on map"
            className="w-full h-48 object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
            data-testid="store-map-preview"
          />
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving || !dirty || !hasCoords || isPending}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="store-location-save"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          {willNeedApproval && !isPending ? 'Submit for admin approval' : 'Update location'}
        </Button>
      </div>
    </section>
  );
};

export default StoreLocationPanel;
